import {Component, computed, ElementRef, HostBinding, Input, OnDestroy, OnInit, ViewChild, AfterViewChecked} from '@angular/core';
import {NavigationEnd, Router, RouterModule} from '@angular/router';
import {animate, AnimationEvent, state, style, transition, trigger} from '@angular/animations';
import {Subscription} from 'rxjs';
import {filter} from 'rxjs/operators';
import {DomHandler} from 'primeng/dom';
import {TooltipModule} from 'primeng/tooltip';
import {CommonModule} from '@angular/common';
import {RippleModule} from 'primeng/ripple';
import {LayoutService} from '@/layout/service/layout.service';

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: '[app-menuitem]',
    imports: [CommonModule, RouterModule, RippleModule, TooltipModule],
    standalone: true,
    template: `
        <ng-container>
            <!-- Texto de grupo cuando no está colapsado -->
            <div *ngIf="root && item.visible !== false && !collapsed" class="layout-menuitem-root-text">
                <span>{{ item.label }}</span>
            </div>
            
            <!-- Enlace sin routerLink -->
            <a
                *ngIf="(!item.routerLink || item.items) && item.visible !== false"
                [attr.href]="item.url"
                (click)="itemClick($event)"
                (mouseenter)="onMouseEnter()"
                (mouseleave)="onMouseLeave()"
                [ngClass]="item.class"
                [attr.target]="item.target"
                tabindex="0"
                pRipple
                [pTooltip]="collapsed && root ? (item.tooltip || item.label) : ''"
                [tooltipDisabled]="!collapsed || !root"
                tooltipPosition="right">
                
                <i [ngClass]="item.icon" class="layout-menuitem-icon"></i>
                <span class="layout-menuitem-text" *ngIf="!collapsed">{{ item.label }}</span>
                <i class="pi pi-fw pi-angle-down layout-submenu-toggler" 
                   *ngIf="item.items && !collapsed"></i>
            </a>
            
            <!-- Enlace con routerLink -->
            <a
                *ngIf="item.routerLink && !item.items && item.visible !== false"
                (click)="itemClick($event)"
                (mouseenter)="onMouseEnter()"
                (mouseleave)="onMouseLeave()"
                [ngClass]="item.class"
                [routerLink]="item.routerLink"
                routerLinkActive="active-route"
                [routerLinkActiveOptions]="
                    item.routerLinkActiveOptions || {
                        paths: 'exact',
                        queryParams: 'ignored',
                        matrixParams: 'ignored',
                        fragment: 'ignored'
                    }
                "
                [fragment]="item.fragment"
                [queryParamsHandling]="item.queryParamsHandling"
                [preserveFragment]="item.preserveFragment"
                [skipLocationChange]="item.skipLocationChange"
                [replaceUrl]="item.replaceUrl"
                [state]="item.state"
                [queryParams]="item.queryParams"
                [attr.target]="item.target"
                tabindex="0"
                pRipple
                [pTooltip]="collapsed && root ? (item.tooltip || item.label) : ''"
                [tooltipDisabled]="!collapsed || !root"
                tooltipPosition="right">
                
                <i [ngClass]="item.icon" class="layout-menuitem-icon"></i>
                <span class="layout-menuitem-text" *ngIf="!collapsed">{{ item.label }}</span>
            </a>

            <!-- Submenu normal (cuando no está colapsado) -->
            <ul #submenu 
                *ngIf="item.items && item.visible !== false && !collapsed && active" 
                [@children]="submenuAnimation" 
                (@children.done)="onSubmenuAnimated($event)">
                <ng-template ngFor let-child let-i="index" [ngForOf]="item.items">
                    <li app-menuitem 
                        [item]="child" 
                        [index]="i" 
                        [parentKey]="key" 
                        [collapsed]="collapsed"
                        [class]="child['badgeClass']"></li>
                </ng-template>
            </ul>

            <!-- Submenu colapsado (aparece con CLICK solamente) -->
            <div #collapsedSubmenu 
                 *ngIf="item.items && item.visible !== false && collapsed && showCollapsedSubmenu" 
                 class="collapsed-submenu-overlay"
                 (mouseleave)="onSubmenuMouseLeave()">
                <div class="submenu-header">
                    <i [ngClass]="item.icon"></i>
                    <span>{{ item.label }}</span>
                </div>
                <div class="submenu-content">
                    <a *ngFor="let child of item.items" 
                       [routerLink]="child.routerLink" 
                       routerLinkActive="active"
                       [routerLinkActiveOptions]="{ exact: true }"
                       (click)="hideCollapsedSubmenu()">
                        <i [class]="child.icon" *ngIf="child.icon"></i>
                        <span>{{ child.label }}</span>
                    </a>
                </div>
            </div>
        </ng-container>
    `,
    animations: [
        trigger('children', [
            state('collapsed', style({ height: '0' })),
            state('expanded', style({ height: '*' })),
            state('hidden', style({ display: 'none' })),
            state('visible', style({ display: 'block' })),
            transition('collapsed <=> expanded', animate('400ms cubic-bezier(0.86, 0, 0.07, 1)'))
        ])
    ]
})
export class AppMenuitem implements OnInit, OnDestroy, AfterViewChecked {
    @Input() item: any;
    @Input() index!: number;
    @Input() @HostBinding('class.layout-root-menuitem') root!: boolean;
    @Input() parentKey!: string;
    @Input() collapsed = false;

    @ViewChild('submenu') submenu!: ElementRef;
    @ViewChild('collapsedSubmenu') collapsedSubmenu!: ElementRef;

    @HostBinding('class.active-menuitem')
    get activeClass() {
        return this.active;
    }

    @HostBinding('class.has-active-child')
    get hasActiveChild() {
        return this.hasActiveRoute() && this.root;
    }

    /* Item raíz que es un enlace directo, sin submenú (Inicio, Volver a
       Proyectos). Se renderiza como opción normal, no como cabecera de grupo. */
    @HostBinding('class.layout-root-link')
    get isRootLink() {
        return this.root && !!this.item?.routerLink && !this.item?.items;
    }

    active = false;
    showCollapsedSubmenu = false;
    submenuTimeout: any;
    menuSourceSubscription: Subscription;
    menuResetSubscription: Subscription;
    key: string = '';

    get submenuAnimation() {
        if (this.layoutService.isDesktop() && (this.layoutService.isHorizontal() || this.layoutService.isSlim() || this.layoutService.isSlimPlus())) {
            return this.active ? 'visible' : 'hidden';
        } else return this.root ? 'expanded' : this.active ? 'expanded' : 'collapsed';
    }

    isSlim = computed(() => this.layoutService.isSlim());
    isSlimPlus = computed(() => this.layoutService.isSlimPlus());
    isHorizontal = computed(() => this.layoutService.isHorizontal());

    get isDesktop() {
        return this.layoutService.isDesktop();
    }

    get isMobile() {
        return this.layoutService.isMobile();
    }

    constructor(
        public layoutService: LayoutService,
        public router: Router
    ) {
        this.menuSourceSubscription = this.layoutService.menuSource$.subscribe((value) => {
            Promise.resolve(null).then(() => {
                if (value.routeEvent) {
                    this.active = value.key === this.key || value.key.startsWith(this.key + '-') ? true : false;
                } else {
                    if (value.key !== this.key && !value.key.startsWith(this.key + '-')) {
                        this.active = false;
                    }
                }
            });
        });

        this.menuResetSubscription = this.layoutService.resetSource$.subscribe(() => {
            this.active = false;
        });

        this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((params) => {
            if (this.isSlimPlus() || this.isSlim() || this.isHorizontal()) {
                this.active = false;
            } else {
                if (this.item.routerLink) {
                    this.updateActiveStateFromRoute();
                }
            }
        });
    }

    ngOnInit() {
        this.key = this.parentKey ? this.parentKey + '-' + this.index : String(this.index);

        if (!(this.isSlimPlus() || this.isSlim() || this.isHorizontal()) && this.item.routerLink) {
            this.updateActiveStateFromRoute();
        }
    }

    ngAfterViewChecked() {
        if (this.root && this.active && this.isDesktop) {
            if (this.submenu?.nativeElement && (this.isHorizontal() || this.isSlim() || this.isSlimPlus())) {
                this.calculatePosition(this.submenu.nativeElement, this.submenu.nativeElement.parentElement);
            }
            
            if (this.collapsed && this.collapsedSubmenu?.nativeElement) {
                this.calculateCollapsedPosition(this.collapsedSubmenu.nativeElement);
            }
        }
    }

    hasActiveRoute(): boolean {
        if (this.item.routerLink) {
            return this.router.isActive(this.item.routerLink[0], {
                paths: 'subset',
                queryParams: 'ignored',
                matrixParams: 'ignored',
                fragment: 'ignored'
            });
        }

        if (this.item.items) {
            return this.item.items.some((child: any) => {
                if (child.routerLink) {
                    return this.router.isActive(child.routerLink[0], {
                        paths: 'subset',
                        queryParams: 'ignored',
                        matrixParams: 'ignored',
                        fragment: 'ignored'
                    });
                }
                return false;
            });
        }

        return false;
    }

    updateActiveStateFromRoute() {
        let activeRoute = this.router.isActive(this.item.routerLink[0], {
            paths: 'exact',
            queryParams: 'ignored',
            matrixParams: 'ignored',
            fragment: 'ignored'
        });

        if (activeRoute) {
            this.layoutService.onMenuStateChange({
                key: this.key,
                routeEvent: true
            });
        }
    }

    onSubmenuAnimated(event: AnimationEvent) {
        if (event.toState === 'visible' && this.isDesktop && (this.isHorizontal() || this.isSlim() || this.isSlimPlus())) {
            const el = <HTMLUListElement>event.element;
            const elParent = <HTMLUListElement>el.parentElement;
            this.calculatePosition(el, elParent);
        }
    }

    calculatePosition(overlay: HTMLElement, target: HTMLElement) {
        if (overlay) {
            const { left, top } = target.getBoundingClientRect();
            const [vWidth, vHeight] = [window.innerWidth, window.innerHeight];
            const [oWidth, oHeight] = [overlay.offsetWidth, overlay.offsetHeight];
            const scrollbarWidth = DomHandler.calculateScrollbarWidth();
            
            overlay.style.top = '';
            overlay.style.left = '';

            if (this.isHorizontal()) {
                const width = left + oWidth + scrollbarWidth;
                overlay.style.left = vWidth < width ? `${left - (width - vWidth)}px` : `${left}px`;
            } else if (this.isSlim() || this.isSlimPlus()) {
                const height = top + oHeight;
                const finalTop = vHeight < height ? top - (height - vHeight) : top;
                overlay.style.top = `${Math.max(8, finalTop)}px`;
                const finalLeft = Math.round(left + target.offsetWidth);
                overlay.style.left = `${finalLeft}px`;
            }
        }
    }

    calculateCollapsedPosition(overlay: HTMLElement) {
        if (overlay && overlay.parentElement) {
            const target = overlay.parentElement;
            const { left, top, width: targetWidth } = target.getBoundingClientRect();
            const [vWidth, vHeight] = [window.innerWidth, window.innerHeight];
            const [oWidth, oHeight] = [overlay.offsetWidth, overlay.offsetHeight];
            
            overlay.style.left = `${left + targetWidth + 8}px`;
            
            const finalTop = vHeight < top + oHeight ? top - (oHeight - vHeight + top) : top;
            overlay.style.top = `${Math.max(8, finalTop)}px`;
        }
    }

    itemClick(event: Event) {
        if (this.item.disabled) {
            event.preventDefault();
            return;
        }

        // En modo colapsado, toggle del submenu con click
        if (this.collapsed && this.item.items && this.root) {
            event.preventDefault();
            this.showCollapsedSubmenu = !this.showCollapsedSubmenu;
            return;
        }

        if ((this.root && this.isSlim()) || this.isHorizontal() || this.isSlimPlus()) {
            this.layoutService.layoutState.update((val) => ({
                ...val,
                menuHoverActive: !val.menuHoverActive
            }));
        }

        if (this.item.command) {
            this.item.command({ originalEvent: event, item: this.item });
        }

        if (this.item.items) {
            this.active = !this.active;

            if (this.root && this.active && (this.isSlim() || this.isHorizontal() || this.isSlimPlus())) {
                this.layoutService.onOverlaySubmenuOpen();
            }
        } else {
            if (this.layoutService.isMobile()) {
                this.layoutService.layoutState.update((val) => ({
                    ...val,
                    staticMenuMobileActive: false
                }));
            }

            if (this.isSlim() || this.isHorizontal() || this.isSlimPlus()) {
                this.layoutService.reset();
                this.layoutService.layoutState.update((val) => ({
                    ...val,
                    menuHoverActive: false
                }));
            }
        }

        this.layoutService.onMenuStateChange({ key: this.key });
    }

    onMouseEnter() {
        // Solo para modos slim, horizontal, etc. NO para collapsed
        if (this.root && (this.isSlim() || this.isHorizontal() || this.isSlimPlus()) && this.layoutService.isDesktop() && !this.collapsed) {
            if (this.layoutService.layoutState().menuHoverActive) {
                this.active = true;
                this.layoutService.onMenuStateChange({ key: this.key });
            }
        }
    }

    onMouseLeave() {
        // No hacer nada en modo colapsado
    }

    onSubmenuMouseLeave() {
        // Ocultar submenu cuando sale del submenu colapsado
        this.submenuTimeout = setTimeout(() => {
            this.showCollapsedSubmenu = false;
        }, 100);
    }

    hideCollapsedSubmenu() {
        this.showCollapsedSubmenu = false;
    }

    ngOnDestroy() {
        if (this.menuSourceSubscription) {
            this.menuSourceSubscription.unsubscribe();
        }

        if (this.menuResetSubscription) {
            this.menuResetSubscription.unsubscribe();
        }

        if (this.submenuTimeout) {
            clearTimeout(this.submenuTimeout);
        }
    }
}